import React from 'react';

/**
 * School highlights — verified signals block.
 *
 * Tiers:
 *   - "basic" (RM 4.99): only the top-matched school sees this section, and
 *     only the exam-results headline is shown. All other signals are hidden.
 *   - "full"  (RM 10):   every matched card shows every available signal.
 *   - "locked": component returns null (rendered behind paywall already).
 *
 * Renders nothing when no relevant signals exist for the school.
 */
const COBIS_MAP = {
  beacon: {
    title: '⭐ COBIS Beacon School',
    subtitle: 'Top UK accreditation for student development and leadership',
  },
  double_beacon: {
    title: '⭐ COBIS Double Beacon Status',
    subtitle: "One of only 6 schools globally — UK's highest recognition for student welfare and leadership",
  },
  patron: {
    title: "⭐ COBIS Patron's Accreditation",
    subtitle: 'Highest tier of UK British international school quality — one of first in Asia',
  },
  member: {
    title: '✅ COBIS Member',
    subtitle: 'Council of British International Schools — quality assurance for British international schools',
  },
};

const SchoolHighlights = ({ school, tier = 'full', compact = false, justUnlocked = false }) => {
  if (tier === 'locked') return null;

  const attrs = school?.attributes_snapshot || {};
  const headline = attrs.exam_headline ?? school?.exam_headline;
  const benchmark = attrs.exam_benchmark ?? school?.exam_benchmark;
  const examYear = attrs.exam_year ?? school?.exam_year;
  const confidence = attrs.exam_results_strength_confidence ?? school?.exam_results_strength_confidence;

  const rows = [];

  // ---- Exam results — present on both tiers ----
  if (headline && (typeof confidence !== 'number' || confidence >= 0.7)) {
    rows.push({
      key: 'exam',
      title: benchmark ? `📊 ${headline} (vs ${benchmark})` : `📊 ${headline}`,
      subtitle: examYear
        ? `Source: official school results ${examYear}`
        : 'Source: official school results',
    });
  }

  if (tier === 'basic') {
    // Basic tier: exam-only, top match only — caller decides which card gets it.
    if (!rows.length) return null;
    return (
      <Wrapper compact={compact} schoolId={school?.id} rows={rows} justUnlocked={false} basicRowCount={rows.length} />
    );
  }

  // ---- Full tier signals (display order locked per spec) ----
  if (school?.cis_accredited) {
    rows.push({
      key: 'cis',
      title: '✅ CIS Accredited',
      subtitle: 'Council of International Schools — globally recognised accreditation',
    });
  }
  if (school?.apple_distinguished_school) {
    rows.push({
      key: 'apple',
      title: '🍎 Apple Distinguished School',
      subtitle: 'Recognised for innovation in learning',
    });
  }
  if (school?.microsoft_showcase_school) {
    rows.push({
      key: 'microsoft',
      title: '💻 Microsoft Showcase School',
      subtitle: 'Global leader in digital transformation',
    });
  }
  if (school?.cobis_tier && COBIS_MAP[school.cobis_tier]) {
    rows.push({ key: 'cobis', ...COBIS_MAP[school.cobis_tier] });
  }
  if (school?.isqm_gold) {
    rows.push({
      key: 'isqm',
      title: '🏅 ISQM Gold',
      subtitle: 'Rated outstanding across all UK school quality standards — highest possible rating',
    });
  }
  if (school?.fobisia_member) {
    rows.push({
      key: 'fobisia',
      title: '🌏 FOBISIA Member',
      subtitle: 'Federation of British International Schools in Asia',
    });
  }
  if (school?.mun_host || school?.mun_active) {
    rows.push({
      key: 'mun',
      title: '🎓 Model United Nations',
      subtitle: 'Active MUN programme with international awards',
    });
  }
  if (school?.unique_signal) {
    rows.push({
      key: 'unique',
      title: `✨ ${school.unique_signal}`,
      subtitle: null,
    });
  }

  if (!rows.length) return null;
  // Number of rows visible on the basic tier (exam only). Any rows beyond
  // this index are unlocked by the Full report — animate them in when
  // `justUnlocked` is true so the parent can see exactly what changed.
  const basicRowCount = headline && (typeof confidence !== 'number' || confidence >= 0.7) ? 1 : 0;
  return <Wrapper compact={compact} schoolId={school?.id} rows={rows} justUnlocked={justUnlocked} basicRowCount={basicRowCount} />;
};

const Wrapper = ({ compact, schoolId, rows, justUnlocked = false, basicRowCount = 0 }) => {
  const padding = compact ? 'px-4 py-4' : 'px-5 sm:px-6 py-5';
  const headerCls = compact
    ? 'text-[11px] font-bold uppercase tracking-[0.18em] text-[#6B6560] mb-3'
    : 'text-xs font-bold uppercase tracking-[0.2em] text-[#6B6560] mb-3';

  return (
    <div
      className={`${padding} rounded-xl bg-white border border-[#E8DED3]`}
      data-testid={`school-highlights-${schoolId || 'x'}`}
    >
      <h4 className={headerCls}>School highlights</h4>
      <ul className="space-y-3">
        {rows.map((r, idx) => {
          // Rows beyond `basicRowCount` are newly visible post-upgrade.
          // Staggered 80ms delay per row for a pleasant reveal cascade.
          const isNewlyRevealed = justUnlocked && idx >= basicRowCount;
          const delay = isNewlyRevealed ? (idx - basicRowCount) * 80 : 0;
          return (
            <li
              key={r.key}
              className={
                'text-sm text-[#2C2823] ' +
                (isNewlyRevealed ? 'animate-fade-in-up' : '')
              }
              style={isNewlyRevealed ? { animationDelay: `${delay}ms`, animationFillMode: 'both' } : undefined}
              data-testid={`school-highlight-${r.key}-${schoolId || 'x'}`}
            >
              <p className="font-semibold leading-snug">{r.title}</p>
              {r.subtitle && (
                <p className="text-[12.5px] text-[#6B6560] leading-snug mt-0.5">{r.subtitle}</p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SchoolHighlights;
